import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { formatMoney, formatQty } from '@shop/shared'
import { Banner, Empty, ErrorBanner, Spinner } from '@shop/ui'
import { Figure, Sheet, TouchButton } from '../pos/touch'
import * as pos from '../pos/api'
import type { CashSession, PosDashboard, SalesReturnHeader } from '../pos/contracts'
import Payouts from './Payouts'
import AttendancePanel from './AttendancePanel'

/** ساعةُ فتح الورديّة بتوقيت عمّان — «9:14 ص» بأرقامٍ لاتينيّة كبقيّة النقطة، لا تاريخٌ كامل: الورديّةُ من اليوم نفسِه. */
const clock = new Intl.DateTimeFormat('ar-JO-u-nu-latn', { timeZone: 'Asia/Amman', hour: 'numeric', minute: '2-digit' })

function formatClock(iso: string): string {
  return clock.format(new Date(iso))
}

/**
 * لوحةُ الصندوق المختصرة — <b>أرقامُ اليوم، وما ينتظر إنسانًا</b>.
 *
 * ⚠ <b>ولا كلفةَ ولا هامشَ لمن لا يملك <c>catalog.product.cost.view</c></b> —
 *   والخادمُ يردّهما <c>null</c>، فالشاشةُ لا تعرض ما لم يصل. <b>ولا تُخفي
 *   الشاشةُ ما وصل</b>: الحجبُ هناك لا هنا.
 *
 * <b>و«ما ينتظر المشرف» ليس رقمًا يُقرأ فقط</b>: من يملك ردَّ المال يردّه من
 * هنا بضغطة — وإلا بقي الزبونُ ينتظر مالَه ولا أحدَ يعلم.
 */
export default function Dashboard({
  canRefund,
  canPayout,
  canPunch,
  session,
  onClose,
}: {
  canRefund: boolean
  canPayout: boolean
  canPunch: boolean
  session: CashSession
  onClose: () => void
}) {
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const queries = useQueryClient()

  const summary = useQuery({ queryKey: ['board'], queryFn: () => pos.dashboard() })

  // ⚠ **ومبيعاتُ الورديّة لا مبيعاتُ اليوم.** الورديّةُ قد تمتدّ إلى ما بعد
  //   منتصف الليل، واليومُ قد يحمل ورديّتين — والكاشيرُ الذي يسأل «كم بعتُ؟»
  //   يقصد ورديّتَه التي بين يديه وما في درجها.
  // ⚠ **وكلُّ فاتورةٍ تحمل موادَّها وطرقَ دفعها** — طلبُ المالك ٢٠٢٦-٠٩-٢٨.
  //   وتصل كلُّها في رحلةٍ واحدة لا رحلةٍ لكلّ فاتورة.
  const mine = useQuery({
    queryKey: ['shift-invoices', session.id],
    queryFn: ({ signal }) => pos.shiftInvoices(session.id, signal),
    staleTime: 0,
  })

  // ⚠ **والمواد مُجمَّعةً في الخادم** — جمعُها في الجهاز عشرون رحلةً على شبكة المحلّ.
  const items = useQuery({
    queryKey: ['shift-items', session.id],
    queryFn: () => pos.shiftItems(session.id),
    staleTime: 0,
  })

  // ⚠ **والصافي يُقرأ من الخادم لا يُطرح في الجهاز.** «مبيعاتي ناقصَ ما
  //   أراه من مرتجعات» رقمٌ خاطئ: المرتجعُ يُنسَب إلى من رحّله، وقد يُرحَّل في
  //   ورديّتي مرتجعُ بيعةٍ ليست لي. والمنظرُ في القاعدة هو الذي يحسمها.
  const report = useQuery({
    queryKey: ['shift-report', session.id],
    queryFn: () => pos.shiftReport(session.id),
    staleTime: 0,
  })

  const shift = report.data ?? null

  const sales = mine.data?.rows ?? []
  const sold = items.data ?? []
  const shiftTotal = sales.reduce((sum, row) => sum + row.total, 0)

  // ⚠ **ولا تُسأل قائمةُ المرتجعات لمن لا يملك ردَّ المال.** كانت تُسأل دائمًا
  //   فيردّ الخادمُ ٤٠٣، ويظهر في لوحةٍ تعمل بدونها **خطأٌ أحمرُ لا يفعل
  //   صاحبُه شيئًا حياله**. ورسالةُ المنع صارت تُسمّي الصلاحيّة، فبانت.
  const posted = useQuery({
    queryKey: ['returns', 'POSTED'],
    queryFn: () => pos.listReturns('POSTED'),
    enabled: canRefund,
  })

  const board: PosDashboard | null = summary.data ?? null

  // **وما ينتظر المشرف** يُشتقّ من المرتجعات المُرحَّلة — لا يُخزَّن.
  const pending: SalesReturnHeader[] = (posted.data ?? []).filter(
    (row) => row.refundState === 'PENDING',
  )

  const refund = async (row: SalesReturnHeader) => {
    setBusy(row.id)
    setError(null)

    try {
      // **وكاملٌ دائمًا**: مجموعُ المرتجع كلِّه، ولا جزئيّ.
      await pos.refundReturn(row.id, [{ methodCode: 'CASH', amount: row.total }])

      await Promise.all([
        queries.invalidateQueries({ queryKey: ['board'] }),
        queries.invalidateQueries({ queryKey: ['returns'] }),
      ])
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(null)
    }
  }

  return (
    <Sheet title={`ورديّتي ${session.sessionNo}`} onClose={onClose}>
      <div className="mx-auto grid max-w-md gap-4">
        <ErrorBanner
          error={error ?? summary.error ?? posted.error ?? mine.error ?? items.error ?? report.error}
        />

        {/* ── الدوامُ اليوم — طلبُ المالك ٢٠٢٦-١٠-٠٨ ───────────────────────── */}
        {canPunch && <AttendancePanel />}

        {/* ── بكم فُتحت الورديّة ومتى ─────────────────────────────────────
            ⚠ طلبُ المالك ٢٠٢٦-١٠-٠٤. وكان مبلغُ الفتح لا يظهر إلا عند الإغلاق —
              والكاشيرُ يحتاجه أثناء الورديّة ليعرف ما في الدرج من أوّل اليوم. */}
        <div className="grid grid-cols-2 gap-2">
          <Figure label="فُتحت الورديّةُ بـ" value={formatMoney(session.openingCash)} />
          <Figure label="فُتحت الساعة" value={formatClock(session.openedAt)} />
        </div>

        {/* ── ما خرج من الدرج — طلبُ المالك ٢٠٢٦-١٠-٠٧ ───────────────────── */}
        {canPayout && <Payouts sessionId={session.id} report={shift} />}

        {/* ── مبيعاتُ هذه الورديّة ───────────────────────────────────────── */}
        <section className="grid gap-2">
          <div className="grid grid-cols-2 gap-2">
            <Figure label="مبيعاتُ ورديّتي" value={formatMoney(shiftTotal)} tone="ok" />
            <Figure label="فواتيري" value={String(sales.length)} />
          </div>

          {/* ⚠ **والصافي رقمٌ ثالثٌ لا بديلٌ عن المبيعات**: ورديّةٌ باعت مئةً
              وردّت تسعين صافيها عشرة — والرقمان معًا يقولان ما جرى، وأحدُهما
              وحدَه يُخفي نصفَه. ولذلك لا يظهر إلا حين يكون هناك مرتجعٌ فعلًا،
              فلا يزدحم على الكاشير رقمٌ يساوي الذي فوقه. */}
          {shift && shift.returnsTotal > 0 && (
            <div className="grid grid-cols-2 gap-2">
              <Figure label="مرتجعاتُ الورديّة" value={formatMoney(shift.returnsTotal)} tone="warn" />
              <Figure label="صافي الورديّة" value={formatMoney(shift.netSales)} tone="ok" />
            </div>
          )}

          {shift && shift.returnsTotal === 0 && (
            <Figure label="صافي الورديّة" value={formatMoney(shift.netSales)} tone="ok" />
          )}

          {mine.isPending ? (
            <Spinner label="تُقرأ فواتيرُ الورديّة…" />
          ) : sales.length === 0 ? (
            <Empty>لم تبع شيئًا في هذه الورديّة بعد.</Empty>
          ) : (
            <ul className="grid gap-1">
              {sales.map((row) => (
                <li key={row.saleNo} className="rounded-lg border border-line px-3 py-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="tabular text-sm font-semibold" dir="ltr">{row.saleNo}</span>

                    {row.customerName && (
                      <span className="min-w-0 flex-1 truncate text-xs text-ink-soft">
                        {row.customerName}
                      </span>
                    )}

                    <span className="tabular text-base font-bold" dir="ltr">
                      {formatMoney(row.total)}
                    </span>
                  </div>

                  {/* الموادّ — بالسعر الذي بيعت به بعد الخصم. */}
                  <ul className="mt-1 grid gap-0.5 border-t border-line/50 pt-1 text-xs">
                    {row.items.map((item, index) => (
                      <li key={index} className="flex items-baseline gap-2">
                        <span className="tabular shrink-0 text-ink-soft" dir="ltr">×{formatQty(item.qty)}</span>
                        <span className="min-w-0 flex-1 truncate">{item.productName}</span>
                        {item.discount > 0 && (
                          <span className="shrink-0 text-warn">خصم {formatMoney(item.discount)}</span>
                        )}
                        <span className="tabular shrink-0" dir="ltr">{formatMoney(item.lineTotal)}</span>
                      </li>
                    ))}
                  </ul>

                  {/* وطريقةُ الدفع — والفاتورةُ قد تُدفع بأكثرَ من طريقة. */}
                  <div className="mt-1 flex flex-wrap gap-1 text-xs">
                    {row.payments.map((payment) => (
                      <span
                        key={payment.methodName}
                        className="rounded bg-surface-raised px-2 py-0.5 text-ink-soft"
                      >
                        {payment.methodName} <b className="tabular" dir="ltr">{formatMoney(payment.amount)}</b>
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ── ما بِيع من موادّ في هذه الورديّة ───────────────────────────── */}
        {sold.length > 0 && (
          <section className="grid gap-2">
            <h3 className="text-sm font-semibold">المواد التي بِعتُها</h3>

            {/* ⚠ **والمردودُ مخصومٌ من الكمّيّة.** «بِعتُ قطعتين» وقد رُدّت
                واحدةٌ في الورديّة نفسِها خبرٌ خاطئ: على الرفّ نقصت واحدةٌ لا
                اثنتان، وفي الدرج مالُ واحدة. **ويُعرض ما رُدّ ولا يُخفى** —
                رقمٌ نقص بلا سببٍ ظاهرٍ يبدو خطأً في النظام. */}

            <ul className="grid gap-1">
              {sold.map((row) => (
                <li
                  key={row.variantId}
                  className="flex items-baseline gap-2 rounded-lg border border-line px-3 py-2"
                >
                  <span
                    className={`tabular shrink-0 rounded px-2 text-sm ${
                      row.qty < 0 ? 'bg-warn/20 text-warn' : 'bg-surface-raised'
                    }`}
                    dir="ltr"
                  >
                    ×{formatQty(row.qty)}
                  </span>

                  <span className="min-w-0 flex-1 truncate text-sm">
                    {row.productName}
                    {row.returnedQty > 0 && (
                      <span className="text-warn"> · رُدّ {formatQty(row.returnedQty)}</span>
                    )}
                  </span>

                  <span className="tabular shrink-0 text-sm font-bold" dir="ltr">
                    {formatMoney(row.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="border-t border-line" />

        <h3 className="text-sm font-semibold text-ink-soft">أرقامُ اليوم كلِّه</h3>

        {summary.isPending && <Spinner />}

        {board && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Figure label="الدخل" value={formatMoney(board.revenue)} />
              <Figure label="العمليّات" value={String(board.transactions)} />
              <Figure label="متوسّطُ السلّة" value={formatMoney(board.averageBasket)} />
              <Figure label="المرتجعات" value={String(board.returnsToday)} />
              <Figure label="نقدًا" value={formatMoney(board.cashIn)} />
              <Figure label="بطاقةً وكليك" value={formatMoney(board.nonCashIn)} />
            </div>

            {board.margin !== null && (
              <div className="grid grid-cols-2 gap-2">
                <Figure label="الكلفة" value={formatMoney(board.costToday)} />
                <Figure label="الهامش" value={formatMoney(board.margin)} tone="ok" />
              </div>
            )}

            {/* ⚠ **والهامشُ لا يُقرأ وحدَه**: سطورٌ بلا كلفةٍ تجعله أعلى من
                الحقيقة، وعرضُه بلا هذا التنبيه جوابٌ كاذبٌ لا يعرف أحدٌ أنّه
                كاذب. */}
            {board.margin !== null && board.uncostedLines > 0 && (
              <Banner tone="warn">
                {board.uncostedLines} سطرًا بلا كلفة — <b>والهامشُ أعلى من الحقيقة</b>.
              </Banner>
            )}

            {board.negativeStockVariants > 0 && (
              <Banner tone="warn">
                {board.negativeStockVariants} صنفًا رصيدُه سالبٌ الآن.
              </Banner>
            )}
          </>
        )}

        <section className="grid gap-2">
          <h3 className="text-sm font-semibold">
            ما ينتظر المشرف
            {board && board.pendingRefunds > 0 && (
              <span className="tabular mr-2 rounded-full bg-warn/20 px-2 py-0.5 text-xs" dir="ltr">
                {board.pendingRefunds} · {formatMoney(board.pendingRefundAmount)}
              </span>
            )}
          </h3>

          {pending.length === 0 ? (
            <Empty>لا مرتجعَ ينتظر مالًا.</Empty>
          ) : (
            <ul className="grid gap-2">
              {pending.map((row) => (
                <li
                  key={row.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-warn/40 bg-warn/5 p-3"
                >
                  <div>
                    <p className="tabular font-bold" dir="ltr">
                      {row.returnNo}
                    </p>
                    <p className="tabular text-xs text-ink-soft" dir="ltr">
                      {formatMoney(row.total, row.currency)}
                    </p>
                  </div>

                  {canRefund ? (
                    <TouchButton
                      tone="primary"
                      disabled={busy === row.id}
                      onClick={() => void refund(row)}
                    >
                      {busy === row.id ? 'يُردّ…' : 'ردّ المال'}
                    </TouchButton>
                  ) : (
                    <span className="text-xs text-ink-soft">ينتظر المشرف</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Sheet>
  )
}
